import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { authService } from '../services/authService';

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);

  // Surface SSO errors handed back by the backend OAuth callback
  // (e.g. /login?error=google_auth_failed&error_description=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    if (ssoError) {
      const description = params.get('error_description');
      setError(description || 'Single sign-on failed. Please try again.');
      // Clean the error params out of the URL.
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await onLogin(username, password);
    
    if (!result.success) {
      setError(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('support@itp360.com');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Welcome */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden" style={{background: 'linear-gradient(to right, #4834d4, #341f97)'}}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="text-center text-white relative z-10">
          <img 
            src="/logoBlack.png" 
            alt="ITP360" 
            className="h-16 w-auto mx-auto mb-8 filter brightness-0 invert"
          />
          <h1 className="text-4xl font-bold mb-4">Welcome back</h1>
          <p className="text-white text-xl font-light opacity-90">
            Access your ITP360 portal
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logoBlack.png" 
              alt="ITP360" 
              className="h-12 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold text-gray-900">Sign in to ITP360</h1>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Sign in</h2>
            <p className="text-gray-600 mt-2">Access your account dashboard</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                background: 'linear-gradient(to right, #4834d4, #341f97)',
                "--tw-ring-color": "#4834d4"
              } as React.CSSProperties & {"--tw-ring-color": string}}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #341f97, #2a1a7a)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #4834d4, #341f97)';
              }}
            >
              {loading ? (
                <LoadingSpinner size="small" className="text-white" />
              ) : (
                'Sign in'
              )}
            </button>

            {/* SSO divider */}
            <div className="flex items-center">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="px-3 text-xs font-medium text-gray-400">OR</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Google SSO */}
            <button
              type="button"
              onClick={() => authService.initiateGoogleSSO()}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            {/* Microsoft SSO */}
            <button
              type="button"
              onClick={() => authService.initiateMicrosoftSSO()}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f25022" d="M0 0h11v11H0z" />
                <path fill="#00a4ef" d="M12 0h11v11H12z" />
                <path fill="#7fba00" d="M0 12h11v11H0z" />
                <path fill="#ffb900" d="M12 12h11v11H12z" />
              </svg>
              Continue with Microsoft
            </button>

            <div className="text-center space-y-3">
              <Link
                to="/forgot-password"
                className="text-sm font-medium transition-colors block"
                style={{color: '#4834d4'}}
                onMouseEnter={(e) => e.currentTarget.style.color = '#341f97'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4834d4'}
              >
                Forgot your password?
              </Link>
              
              <div className="border-t border-gray-100 pt-5 mt-4">
                <div className="text-center mb-3">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Support Center</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href="tel:888-274-4529" 
                    className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                      <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Phone Support</span>
                    <span className="text-xs text-gray-500 font-mono">888-274-4529</span>
                  </a>
                  <div className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md">
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                      <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Email Support</span>
                    <div className="flex items-center space-x-2">
                      <a 
                        href="mailto:support@itp360.com" 
                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        support@itp360.com
                      </a>
                      <button
                        onClick={copyEmail}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
                        title="Copy email address"
                      >
                        {emailCopied ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
